import React, { createContext, useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { onAuthStateChanged, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, isIsolatedSession } from '../firebase'; // Importamos la instancia de auth y detector de sesión
import tokenCache from '../utils/tokenCache'; // 🚀 Token cache para optimización
import debouncer from '../utils/requestDebouncer'; // ⚡ Debouncer para evitar requests duplicados
import { getStorage, getTokenKey } from '../utils/sessionStorage'; // 🔐 Multi-sesión

// 1. Creamos el contexto
export const AuthContext = createContext();

// Hook personalizado para usar el contexto fácilmente en otros componentes
export const useAuth = () => {
    return useContext(AuthContext);
}

// 2. Creamos el "Proveedor" que envolverá nuestra aplicación
export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const tokenRefreshInterval = useRef(null);

    // Función para renovar el token automáticamente
    const refreshToken = async (user) => {
        if (!user) return;
        
        try {
            // Forzar renovación del token
            const token = await user.getIdToken(true);
            getStorage().setItem(getTokenKey(), token);
            return token;
        } catch (error) {
            console.error('❌ Error renovando token:', error);
            getStorage().removeItem(getTokenKey());
            // Si falla la renovación, cerrar sesión
            await signOut(auth);
        }
    };

    // ⚡ OPTIMIZACIÓN: Memoizar setupTokenRefresh para evitar warning de dependencias
    const setupTokenRefresh = useCallback((user) => {
        if (tokenRefreshInterval.current) {
            clearInterval(tokenRefreshInterval.current);
        }
        
        if (user) {
            // Renovar token cada 50 minutos (los tokens de Firebase duran 1 hora)
            tokenRefreshInterval.current = setInterval(() => {
                refreshToken(user);
            }, 50 * 60 * 1000); // 50 minutos
        }
    }, []); // refreshToken es estable dentro del intervalo

    useEffect(() => {
        // onAuthStateChanged es el oyente en tiempo real de Firebase.
        // Se ejecuta cada vez que un usuario inicia o cierra sesión.
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            
            if (user) {
                try {
                    // Obtener el token de Firebase y guardarlo en storage apropiado
                    const token = await user.getIdToken();
                    getStorage().setItem(getTokenKey(), token);
                    
                    // Configurar renovación automática
                    setupTokenRefresh(user);
                } catch (error) {
                    console.error('❌ Error obteniendo token:', error);
                    getStorage().removeItem(getTokenKey());
                }
            } else {
                // Usuario no autenticado, limpiar token y detener renovación
                getStorage().removeItem(getTokenKey());
                
                if (tokenRefreshInterval.current) {
                    clearInterval(tokenRefreshInterval.current);
                    tokenRefreshInterval.current = null;
                }
            }
            
            setLoading(false);
        });

        // Limpiamos el oyente cuando el componente se desmonta para evitar fugas de memoria
        return () => {
            unsubscribe();
            if (tokenRefreshInterval.current) {
                clearInterval(tokenRefreshInterval.current);
            }
        };
    }, [setupTokenRefresh]); // ✅ Incluir setupTokenRefresh como dependencia

    // ⚡ OPTIMIZACIÓN: Memoizar función logout para evitar re-renders
    const logout = useCallback(async () => {
        try {
            // Limpiar intervalo de renovación antes de cerrar sesión
            if (tokenRefreshInterval.current) {
                clearInterval(tokenRefreshInterval.current);
                tokenRefreshInterval.current = null;
            }
            
            // 🚀 Limpiar token cacheado
            tokenCache.clearToken();
            
            await signOut(auth);
            getStorage().removeItem(getTokenKey());
            
            // Si es sesión aislada, limpiar también las marcas de sesión
            if (isIsolatedSession()) {
                sessionStorage.removeItem('isolatedSession');
                sessionStorage.removeItem('sessionId');
            }
        } catch (error) {
            console.error('Error al cerrar sesión:', error);
            getStorage().removeItem(getTokenKey()); // Limpiar token incluso si hay error
            tokenCache.clearToken(); // Limpiar caché también
            throw error; // Re-lanzar el error para que el componente pueda manejarlo
        }
    }, []); // Sin dependencias - la función es estable

    // ⚡ OPTIMIZACIÓN: Memoizar getFreshToken con debouncing para evitar requests duplicados
    const getFreshToken = useCallback(async () => {
        if (!currentUser) {
            throw new Error('Usuario no autenticado');
        }
        
        // Usar debouncer para evitar múltiples requests simultáneos
        return await debouncer.execute(
            `fresh-token-${currentUser.uid}`,
            async () => {
                try {
                    const token = await currentUser.getIdToken(true);
                    getStorage().setItem(getTokenKey(), token);
                    return token;
                } catch (error) {
                    console.error('❌ Error obteniendo token fresco:', error);
                    throw error;
                }
            },
            { useCache: true, cacheDuration: 5000 } // Caché de 5 segundos
        );
    }, [currentUser]);

    // ⚡ OPTIMIZACIÓN: Memoizar signInWithGoogle
    const signInWithGoogle = useCallback(async () => {
        try {
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({
                prompt: 'select_account' // Siempre mostrar selector de cuenta
            });
            
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            
            // Obtener token y guardarlo en storage apropiado
            const token = await user.getIdToken();
            getStorage().setItem(getTokenKey(), token);
            // tokenCache se actualizará automáticamente en el próximo getToken()
            
            return user;
        } catch (error) {
            console.error('❌ Error en Google Sign In:', error);
            throw error;
        }
    }, []); // Sin dependencias - usa auth global

    // ⚡ OPTIMIZACIÓN CRÍTICA: Memoizar el valor del context para evitar re-renders masivos
    // Esto evita que TODOS los componentes que usan useAuth() se re-rendericen innecesariamente
    const value = useMemo(() => ({
        currentUser,
        loading,
        logout,
        getFreshToken,
        signInWithGoogle,
    }), [currentUser, loading, logout, getFreshToken, signInWithGoogle]);

    // No mostramos nada hasta que Firebase haya verificado si hay un usuario conectado
    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};